import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaDownload, FaEye } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const AdminReportPage = () => {
    const [salesSummary, setSalesSummary] = useState(null);
    const [dailySales, setDailySales] = useState([]);
    const [monthlySales, setMonthlySales] = useState([]);
    const [customSales, setCustomSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState({ from: '', to: '' });
    const [activePreset, setActivePreset] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [customLoading, setCustomLoading] = useState(false);
    const [customSort, setCustomSort] = useState({ key: 'createdAt', dir: 'desc' });
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [summaryRes, dailyRes, monthlyRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/sales-summary`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(`${API_BASE_URL}/daily-sales`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(`${API_BASE_URL}/monthly-sales`, { headers: { Authorization: `Bearer ${token}` } })
                ]);
                setSalesSummary(summaryRes.data);
                setDailySales(dailyRes.data);
                setMonthlySales(monthlyRes.data);
            } catch (err) {
                toast.error('Failed to fetch sales data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [API_BASE_URL, token]);

    const isValidRange = () => {
        if (!dateRange.from || !dateRange.to) return false;
        const f = new Date(dateRange.from);
        const t = new Date(dateRange.to);
        if (f > t) return false;
        const diffDays = (t - f) / (1000*60*60*24) + 1;
        return diffDays <= 180;
    };

    const fetchCustomSales = async () => {
        if (!isValidRange()) { toast.warn('Invalid or too large range'); return; }
        setCustomLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/all-orders`, { headers: { Authorization: `Bearer ${token}` } });
            const fromDate = new Date(dateRange.from);
            const toDate = new Date(dateRange.to);
            toDate.setHours(23, 59, 59, 999);
            const filtered = (res.data || []).filter(order => {
                const created = new Date(order.createdAt);
                return created >= fromDate && created <= toDate && order.status !== 'cancelled';
            });
            setCustomSales(sortCustom(filtered, customSort.key, customSort.dir));
        } catch (err) {
            toast.error('Custom range fetch failed');
        } finally {
            setCustomLoading(false);
        }
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        const next = { ...dateRange, [name]: value };
        if (next.from && next.to && new Date(next.from) > new Date(next.to)) {
            [next.from, next.to] = [next.to, next.from];
            toast.info('Dates swapped to maintain order');
        }
        setDateRange(next);
        setActivePreset('');
    };

    const applyPreset = (type) => {
        const today = new Date();
        const fmt = d => d.toISOString().slice(0,10);
        let from, to;
        if (type === 'today') { from = to = fmt(today); }
        else if (type === '7d') { const d=new Date(); d.setDate(d.getDate()-6); from=fmt(d); to=fmt(today); }
        else if (type === '30d') { const d=new Date(); d.setDate(d.getDate()-29); from=fmt(d); to=fmt(today); }
        else if (type === 'mtd') { const d=new Date(today.getFullYear(), today.getMonth(),1); from=fmt(d); to=fmt(today); }
        else if (type === 'prevMonth') { const start=new Date(today.getFullYear(), today.getMonth()-1,1); const end=new Date(today.getFullYear(), today.getMonth(),0); from=fmt(start); to=fmt(end); }
        setDateRange({ from, to });
        setActivePreset(type);
        setTimeout(fetchCustomSales, 0);
    };

    const handleDownloadCSV = () => {
        setDownloading(true);
        const pad = (n) => String(n).padStart(2,'0');
        const formatDate = (iso) => {
            if (!iso) return 'N/A';
            const d = new Date(iso);
            return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        };
        const escapeCell = (val) => {
            if (val === null || val === undefined) return '""';
            const s = String(val).replace(/"/g,'""');
            return `"${s}"`;
        };
        const header = [
            'Sr No',
            'Token',
            'Order ID',
            'Date & Time',
            'Customer Name',
            'Mobile Number',
            'Delivery Address',
            'Total Items',
            'Items Detail',
            'Amount (INR)',
            'Payment Method',
            'Payment Status',
            'Order Status'
        ];
        const body = customSales.map((o, idx) => {
            const addressStr = o.address 
                ? [o.address.house, o.address.area, o.address.landmark, o.address.city, o.address.pincode].filter(Boolean).join(', ')
                : 'N/A';
            
            const itemsStr = (o.items || [])
                .map(i => `${i.name || i.title || i.productName || 'Item'} (x${i.quantity || 1})`)
                .join('; ');
                
            return [
                idx + 1,
                (o.token ? o.token.toUpperCase() : null) || `#${o._id.slice(-8)}`,
                o._id,
                formatDate(o.createdAt),
                o.address?.name || o.userName || 'Customer',
                o.mobile || o.address?.mobile || 'N/A',
                addressStr,
                o.items?.length || 0,
                itemsStr || 'N/A',
                (o.grandTotal !== undefined && o.grandTotal !== null) ? o.grandTotal : (o.totalAmount || 0),
                o.paymentMethod || o.paymentMode || 'COD',
                o.paymentStatus || (o.status === 'delivered' ? 'Paid' : 'Pending'),
                o.status
            ];
        });
        const rows = [header, ...body];
        const csvContent = '\uFEFF' + rows.map(r => r.map(escapeCell).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sales_report_${dateRange.from}_to_${dateRange.to}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => setDownloading(false), 700);
    };

    const sortCustom = (data, key, dir) => {
        return [...data].sort((a,b)=>{
            let av, bv;
            if (key === 'createdAt') { av = new Date(a.createdAt); bv = new Date(b.createdAt); }
            else if (key === 'totalAmount') { av = a.totalAmount; bv = b.totalAmount; }
            else { av = a[key]; bv = b[key]; }
            if (av < bv) return dir==='asc'?-1:1;
            if (av > bv) return dir==='asc'?1:-1;
            return 0;
        });
    };

    const handleSort = (key) => {
        setCustomSort(prev => {
            const dir = prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc';
            setCustomSales(s => sortCustom(s, key, dir));
            return { key, dir };
        });
    };

    const customStats = (() => {
        if (!customSales.length) return null;
        const total = customSales.reduce((sum,o)=>sum+(o.totalAmount||0),0);
        const avg = total / customSales.length;
        return { total, count: customSales.length, avg };
    })();

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };



    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                {/* Heading */}
                <div className="row mb-2">
                    <div className="col-12">
                        <div className="section-title">
                            <h4 className="mt-0">SALES OVERVIEW</h4>
                            <p className="text-muted p-0 m-0">Quick snapshot and export of your sales performance</p>
                        </div>
                    </div>
                </div>

                {/* Sales Summary KPI Row (Cloned from AdminHomePage) */}
                <div className="row g-0 dashboard-kpis-row mt-0 mb-0" style={{ borderTop: '1px solid #cbd5e1' }}>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card h-100">
                            <div className="card-body">
                                <h6>Today's Sales</h6>
                                <h3>{formatCurrency(salesSummary?.todaySales || 0)}</h3>
                                <small>{salesSummary?.todayOrders || 0} orders today</small>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card h-100">
                            <div className="card-body">
                                <h6>This Week</h6>
                                <h3>{formatCurrency(salesSummary?.weekSales || 0)}</h3>
                                <small>{salesSummary?.weekOrders || 0} orders this week</small>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card h-100">
                            <div className="card-body">
                                <h6>This Month</h6>
                                <h3>{formatCurrency(salesSummary?.monthSales || 0)}</h3>
                                <small>{salesSummary?.monthOrders || 0} orders this month</small>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card h-100">
                            <div className="card-body">
                                <h6>Total Sales</h6>
                                <h3>{formatCurrency(salesSummary?.totalSales || 0)}</h3>
                                <small>{salesSummary?.totalOrders || 0} total orders</small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Daily Sales Card Container */}
                <div className="card mb-0" style={{ borderTop: '1px solid #cbd5e1', borderBottom: '1px solid #cbd5e1', borderLeft: 'none', borderRight: 'none', borderRadius: 0, boxShadow: 'none', marginLeft: '-1rem', marginRight: '-1rem' }}>
                    <div className="card-header d-flex align-items-center justify-content-between" style={{ backgroundColor: '#FEF2F2', borderBottom: '1px solid #cbd5e1', height: '52px', padding: '0 1rem' }}>
                        <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: '0.95rem', fontFamily: 'Inter, sans-serif' }}>Last 7 Days</h5>
                    </div>
                    <div className="card-body p-0">
                        <div className="daily-sales-cards-grid">
                            {dailySales.length === 0 ? (
                                <div className="p-3 small text-muted">No daily sales data.</div>
                            ) : (
                                dailySales.map((d, i) => {
                                    const DAY_COLORS = [
                                        { border: '#3b82f6', bg: '#eff6ff', labelColor: '#1e40af', salesColor: '#1d4ed8' },
                                        { border: '#10b981', bg: '#ecfdf5', labelColor: '#065f46', salesColor: '#047857' },
                                        { border: '#f59e0b', bg: '#fffbeb', labelColor: '#92400e', salesColor: '#b45309' },
                                        { border: '#8b5cf6', bg: '#f5f3ff', labelColor: '#5b21b6', salesColor: '#6d28d9' },
                                        { border: '#06b6d4', bg: '#ecfeff', labelColor: '#155e75', salesColor: '#0e7490' },
                                        { border: '#ec4899', bg: '#fdf2f8', labelColor: '#9d174d', salesColor: '#be185d' },
                                        { border: '#ed3237', bg: '#fef2f2', labelColor: '#991b1b', salesColor: '#dc2626' }
                                    ];
                                    const theme = DAY_COLORS[i % DAY_COLORS.length];
                                    const hasSales = (d.sales || 0) > 0;
                                    return (
                                        <div 
                                            key={i} 
                                            className="daily-sales-card" 
                                            tabIndex={0} 
                                            aria-label={`Sales on ${d.date} ${formatCurrency(d.sales)}`}
                                            style={{ 
                                                borderTopColor: theme.border,
                                                backgroundColor: theme.bg
                                            }}
                                        >
                                            <div className="day-label" style={{ color: theme.labelColor }}>{d.date}</div>
                                            <div className="day-sales" style={{ color: hasSales ? theme.salesColor : '#1e293b' }}>
                                                {formatCurrency(d.sales)}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Monthly Sales Card Container */}
                <div className="card mb-0" style={{ borderTop: 'none', borderBottom: '1px solid #cbd5e1', borderLeft: 'none', borderRight: 'none', borderRadius: 0, boxShadow: 'none', marginLeft: '-1rem', marginRight: '-1rem' }}>
                    <div className="card-header d-flex align-items-center justify-content-between" style={{ backgroundColor: '#FEF2F2', borderBottom: '1px solid #cbd5e1', height: '52px', padding: '0 1rem' }}>
                        <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: '0.95rem', fontFamily: 'Inter, sans-serif' }}>Last 6 Months</h5>
                    </div>
                    <div className="card-body p-0">
                        <div className="monthly-sales-cards-grid">
                            {monthlySales.length === 0 ? (
                                <div className="p-3 small text-muted">No monthly data.</div>
                            ) : (
                                monthlySales.map((m, i) => (
                                    <div key={i} className="monthly-sales-card" tabIndex={0} aria-label={`Sales in ${m.month} ${formatCurrency(m.sales)}`}>
                                        <div className="month-label">{m.month}</div>
                                        <div className="month-sales">{formatCurrency(m.sales)}</div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Custom Range Sales & Filters Container */}
                <div className="card mb-4" style={{ borderTop: 'none', borderBottom: '1px solid #cbd5e1', borderLeft: 'none', borderRight: 'none', borderRadius: 0, boxShadow: 'none', marginLeft: '-1rem', marginRight: '-1rem' }}>
                    <div className="card-header d-flex align-items-center justify-content-between" style={{ backgroundColor: '#FEF2F2', borderBottom: '1px solid #cbd5e1', height: '52px', padding: '0 1rem' }}>
                        <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: '0.95rem', fontFamily: 'Inter, sans-serif' }}>
                            Custom Range Sales {dateRange.from && dateRange.to && (<span className="text-muted fw-normal ms-1" style={{ fontSize: '0.85rem' }}>({dateRange.from} to {dateRange.to})</span>)}
                        </h5>
                    </div>
                    <div className="card-body p-0">
                        {/* Filter Panel */}
                        <div className={`p-3 bg-white border-bottom ${!isValidRange() && (dateRange.from && dateRange.to) ? 'invalid-range' : ''}`} style={{ borderColor: '#cbd5e1' }}>
                            <div className="row g-3 align-items-end">
                                <div className="col-12 col-sm-6 col-md-3">
                                    <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        From Date
                                    </label>
                                    <input 
                                        type="date" 
                                        className="form-control search-input-custom px-2" 
                                        name="from" 
                                        value={dateRange.from} 
                                        onChange={handleDateChange} 
                                    />
                                </div>

                                <div className="col-12 col-sm-6 col-md-3">
                                    <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        To Date
                                    </label>
                                    <input 
                                        type="date" 
                                        className="form-control search-input-custom px-2" 
                                        name="to" 
                                        value={dateRange.to} 
                                        onChange={handleDateChange} 
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-lg-3">
                                    <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Quick Presets
                                    </label>
                                    <div className="d-flex gap-1">
                                        <button type="button" className={`report-preset-btn flex-fill ${activePreset === 'today' ? 'active' : ''}`} onClick={()=>applyPreset('today')}>Today</button>
                                        <button type="button" className={`report-preset-btn flex-fill ${activePreset === '7d' ? 'active' : ''}`} onClick={()=>applyPreset('7d')}>7 Days</button>
                                        <button type="button" className={`report-preset-btn flex-fill ${activePreset === '30d' ? 'active' : ''}`} onClick={()=>applyPreset('30d')}>30 Days</button>
                                        <button type="button" className={`report-preset-btn flex-fill ${activePreset === 'mtd' ? 'active' : ''}`} onClick={()=>applyPreset('mtd')}>Month</button>
                                    </div>
                                </div>

                                <div className="col-12 col-md-6 col-lg-3 d-flex gap-2">
                                    <button 
                                        className="report-search-btn flex-fill" 
                                        onClick={fetchCustomSales} 
                                        disabled={!isValidRange() || customLoading}
                                    >
                                        {customLoading ? 'Loading...' : 'Search'}
                                    </button>
                                    <button 
                                        className="report-export-btn flex-fill" 
                                        onClick={handleDownloadCSV} 
                                        disabled={!customSales.length || downloading}
                                    >
                                        <FaDownload style={{ fontSize: '12px' }} /> Export
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Custom Table Results */}
                        {(!dateRange.from || !dateRange.to) && !customSales.length && (
                            <div className="text-center py-4 small text-muted">Select a date range or use a preset to generate a report.</div>
                        )}
                        {isValidRange() && customLoading && (
                            <div className="text-center py-4 small text-muted">Loading custom results...</div>
                        )}
                        {isValidRange() && !customLoading && customSales.length === 0 && (dateRange.from && dateRange.to) && (
                            <div className="text-center py-4 small text-muted">No sales in selected range.</div>
                        )}
                        {customStats && (
                            <div className="row g-0 dashboard-kpis-row border-bottom" style={{ borderTop: 'none', marginLeft: 0, marginRight: 0 }}>
                                <div className="col-12 col-md-4">
                                    <div className="card h-100">
                                        <div className="card-body">
                                            <h6>Total Orders</h6>
                                            <h3>{customStats.count}</h3>
                                            <small>Filtered date range</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="card h-100">
                                        <div className="card-body">
                                            <h6>Total Revenue</h6>
                                            <h3>{formatCurrency(customStats.total)}</h3>
                                            <small>Filtered sales value</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="card h-100">
                                        <div className="card-body">
                                            <h6>Avg Order Value</h6>
                                            <h3>{formatCurrency(Math.round(customStats.avg))}</h3>
                                            <small>Average per order</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {customSales.length > 0 && !customLoading && (
                            <div className="table-responsive">
                                <table className="table table-hover mb-0 report-table align-middle">
                                    <thead>
                                        <tr>
                                            <th className="border-0 ps-3">Order & Customer Info</th>
                                            <th className="border-0">Purchase Summary</th>
                                            <th className="border-0">Status</th>
                                            <th className="border-0 text-end pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customSales.map((order, index) => (
                                            <tr key={order._id}>
                                                <td className="align-middle ps-3 py-3">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <span className="badge bg-light text-secondary border fw-bold px-2 py-1 rounded-0" style={{ borderColor: '#cbd5e1', fontSize: '0.75rem', minWidth: '28px', textAlign: 'center' }}>
                                                            #{index + 1}
                                                        </span>
                                                        <div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="fw-bold text-dark mono" style={{ fontSize: '0.9rem' }}>
                                                                    #{order._id.slice(-8)}
                                                                </span>
                                                                <span className="text-muted small">• {new Date(order.createdAt).toLocaleString('en-IN', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                                            </div>
                                                            <div className="small mt-1 d-flex align-items-center gap-2">
                                                                <strong className="text-dark">{order.address?.name || 'Customer'}</strong>
                                                                <span className="text-primary fw-semibold">({order.mobile || 'N/A'})</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="align-middle">
                                                    <div>
                                                        <strong className="text-success fs-6">
                                                            {formatCurrency(order.totalAmount)}
                                                        </strong>
                                                        <div className="mt-1">
                                                            <span className="badge bg-light text-secondary border px-2 py-1 rounded-0" style={{ borderColor: '#cbd5e1', fontSize: '0.75rem', fontWeight: 500 }}>
                                                                {order.items?.length || 0} {order.items?.length === 1 ? 'item' : 'items'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="align-middle">
                                                    <span className={`status-pill ${order.status}`}>{order.status}</span>
                                                </td>
                                                <td className="align-middle text-end pe-4">
                                                    <div className="order-actions-wrapper justify-content-end">
                                                        <Link
                                                            to={`/admin/orders-details/${order._id}`}
                                                            className="order-action-btn order-action-btn-view"
                                                            title="View Details"
                                                        >
                                                            <FaEye />
                                                        </Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
                <ToastContainer position="top-center" autoClose={2200} />
            </div>
        </Fragment>
    );
};

export default AdminReportPage;